import React, { useMemo, useState } from 'react';
import { MultiSelectFilter } from './MultiSelectFilter';
import { PerformanceFilter } from './PerformanceFilter';
import { FDAvailabilityFilter } from './FDAvailabilityFilter';
import { Button } from '@/components/ui/button';
import { X, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function FilterBar({ filters, setFilters, options }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const activeFilterCount = useMemo(() => {
    return (
      (filters.tls?.length || 0) +
      (filters.gyms?.length || 0) +
      (filters.managers?.length || 0) +
      (filters.sources?.length || 0) +
      (filters.verificationStatuses?.length || 0) +
      (filters.performanceLevels?.length || 0) +
      (filters.customPerformanceRange ? 1 : 0) +
      (filters.fdAvailability?.length || 0) +
      (filters.commitmentStatuses?.length || 0)
    );
  }, [filters]);

  const clearAllFilters = () => {
    setFilters({
      tls: [],
      gyms: [],
      managers: [],
      sources: [],
      verificationStatuses: [],
      performanceLevels: [],
      customPerformanceRange: null,
      fdAvailability: [],
      commitmentStatuses: []
    });
  };

  // Active filter chips for compact view
  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.tls?.length) chips.push({ label: `TL: ${filters.tls.length}`, key: 'tls' });
    if (filters.gyms?.length) chips.push({ label: `Gym: ${filters.gyms.length}`, key: 'gyms' });
    if (filters.managers?.length) chips.push({ label: `Manager: ${filters.managers.length}`, key: 'managers' });
    if (filters.sources?.length) chips.push({ label: `Source: ${filters.sources.length}`, key: 'sources' });
    if (filters.verificationStatuses?.length) chips.push({ label: `Verification: ${filters.verificationStatuses.length}`, key: 'verificationStatuses' });
    if (filters.performanceLevels?.length) chips.push({ label: `Performance: ${filters.performanceLevels.length}`, key: 'performanceLevels' });
    if (filters.customPerformanceRange) chips.push({ label: `Custom: ${filters.customPerformanceRange.min}-${filters.customPerformanceRange.max}%`, key: 'customPerformanceRange' });
    if (filters.fdAvailability?.length) chips.push({ label: `FD: ${filters.fdAvailability.length}`, key: 'fdAvailability' });
    if (filters.commitmentStatuses?.length) chips.push({ label: `Status: ${filters.commitmentStatuses.length}`, key: 'commitmentStatuses' });
    return chips;
  }, [filters]);

  return (
    <Card className="mb-3">
      <CardContent className="py-2 px-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Filter className="h-4 w-4 text-gray-600 flex-shrink-0" />

            {/* Collapsed view: Show active filter chips */}
            {!isExpanded && activeFilterCount > 0 && (
              <div className="flex items-center gap-1 flex-wrap">
                {activeChips.map(chip => (
                  <Badge key={chip.key} variant="secondary" className="text-xs">
                    {chip.label}
                  </Badge>
                ))}
              </div>
            )}
            {!isExpanded && activeFilterCount === 0 && (
              <span className="text-sm text-muted-foreground">No filters applied</span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="h-3 w-3 mr-1" />
                Clear ({activeFilterCount})
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-7 text-xs"
            >
              {isExpanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
              {isExpanded ? 'Collapse' : 'Expand'} Filters
            </Button>
          </div>
        </div>

        {/* Expanded view: Show all filters in compact 2-row grid */}
        {isExpanded && (
          <div className="mt-3 pt-3 border-t">
            <div className="grid grid-cols-4 gap-2">{/* First row: Primary filters */}

              <MultiSelectFilter
                label="Team Leader"
                options={options.tls || []}
                selected={filters.tls || []}
                onChange={(newValue) => setFilters(prev => ({ ...prev, tls: newValue }))}
              />

              <MultiSelectFilter
                label="Gym"
                options={options.gyms || []}
                selected={filters.gyms || []}
                onChange={(newValue) => setFilters(prev => ({ ...prev, gyms: newValue }))}
              />

              <MultiSelectFilter
                label="Manager"
                options={options.managers || []}
                selected={filters.managers || []}
                onChange={(newValue) => setFilters(prev => ({ ...prev, managers: newValue }))}
              />

              <MultiSelectFilter
                label="Source"
                options={options.sources || []}
                selected={filters.sources || []}
                onChange={(newValue) => setFilters(prev => ({ ...prev, sources: newValue }))}
              />
            </div>

            {/* Second row: Additional filters */}
            <div className="grid grid-cols-4 gap-2 mt-2">
              <MultiSelectFilter
                label="Verification Status"
                options={options.verificationStatuses || []}
                selected={filters.verificationStatuses || []}
                onChange={(newValue) => setFilters(prev => ({ ...prev, verificationStatuses: newValue }))}
              />

              <PerformanceFilter
                selected={filters.performanceLevels || []}
                customRange={filters.customPerformanceRange}
                onChange={({ presets, customRange }) => {
                  setFilters(prev => ({
                    ...prev,
                    performanceLevels: presets,
                    customPerformanceRange: customRange
                  }));
                }}
              />

              <FDAvailabilityFilter
                selected={filters.fdAvailability || []}
                onChange={(newValue) => setFilters(prev => ({ ...prev, fdAvailability: newValue }))}
              />

              <MultiSelectFilter
                label="Commitment Status"
                options={options.commitmentStatuses || []}
                selected={filters.commitmentStatuses || []}
                onChange={(newValue) => setFilters(prev => ({ ...prev, commitmentStatuses: newValue }))}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
